import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  AddLocation as AddLocationIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  Room as RoomIcon,
  Search as SearchIcon
} from '@material-ui/icons';
import { googleHybridProvider } from '../MapWithDrawing/Measure/mapUtils';

const ACCENT = '#C2410C';
const PIN = '#EA580C';

const ASSET_TYPES = [
  { value: 'controller', key: 'controllers', label: 'Controller', color: '#2563EB' },
  { value: 'valveBox', key: 'valveBoxes', label: 'Valve box', color: '#16A34A' },
  { value: 'backflow', key: 'backflows', label: 'Backflow', color: '#7C3AED' },
  { value: 'multiport', key: 'multiports', label: 'Multiport', color: '#0D9488' }
];

const emptyAssets = {
  controllers: [],
  valveBoxes: [],
  backflows: [],
  multiports: []
};

const siteLabel = candidate => candidate?.jobSite?.name || 'Unknown jobsite';

const siteAddress = candidate => {
  const site = candidate?.jobSite || {};
  return [site.address, site.city, site.state, site.zip].filter(Boolean).join(', ');
};

const assetLabel = (asset, type, index) => {
  const fallback = `${ASSET_TYPES.find(item => item.value === type)?.label || 'Asset'} ${index + 1}`;
  const makeModel = [asset?.brand, asset?.model].filter(Boolean).join(' ');
  return asset?.name || makeModel || asset?.id || fallback;
};

const hasCoords = candidate =>
  Number.isFinite(Number(candidate?.latitude)) && Number.isFinite(Number(candidate?.longitude));

const assetCoords = asset => {
  const coords = asset?.location?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const lat = Number(coords[1]);
  const lng = Number(coords[0]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return [lat, lng];
};

const pinIcon = selected => {
  const w = selected ? 34 : 24;
  const h = selected ? 46 : 32;
  const fill = selected ? ACCENT : PIN;
  const ring = selected
    ? '<circle cx="12" cy="9.5" r="6.4" fill="none" stroke="#fff" stroke-width="1.6" opacity="0.9"/>'
    : '';
  return L.divIcon({
    className: 'iilr-pin',
    html: `<svg width="${w}" height="${h}" viewBox="0 0 24 32" style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.45));">
      <path d="M12 0C5.9 0 1 4.9 1 11c0 8.2 11 21 11 21s11-12.8 11-21C23 4.9 18.1 0 12 0z" fill="${fill}" stroke="#fff" stroke-width="1.4"/>
      <circle cx="12" cy="10.5" r="4" fill="#fff"/>
      ${ring}
    </svg>`,
    iconSize: [w, h],
    iconAnchor: [w / 2, h]
  });
};

const clusterIcon = cluster =>
  L.divIcon({
    className: 'iilr-cluster',
    html: `<div style="
      width: 38px; height: 38px; border-radius: 50%;
      background: ${ACCENT}; border: 2.5px solid #fff;
      color: #fff; font-weight: 700; font-size: 13px;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 6px rgba(0,0,0,0.45);
    ">${cluster.getChildCount()}</div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19]
  });

/**
 * Full-bleed Leaflet map (same Google Hybrid tiles as Site Takeoff).
 * Shows every pending candidate as a clustered pin; clicking a pin selects it.
 * When a candidate is selected, the site's existing irrigation assets are
 * plotted as colored dots so linking vs. creating is an informed choice.
 */
const ReviewMap = ({ candidates, selected, assets, onSelect }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const assetLayerRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = L.map(containerRef.current, {
      center: [33.505, -112.15],
      zoom: 11,
      maxZoom: 22,
      zoomControl: false,
      attributionControl: false
    });
    L.control.zoom({ position: 'topright' }).addTo(map);
    L.tileLayer(googleHybridProvider.url, {
      attribution: googleHybridProvider.attribution,
      maxNativeZoom: 20,
      maxZoom: 22
    }).addTo(map);

    mapRef.current = map;
    // The dialog animates open; size the map once the pane has real dimensions.
    setTimeout(() => {
      try {
        map.invalidateSize();
      } catch (_) {}
    }, 250);

    return () => {
      try {
        map.remove();
      } catch (_) {}
      mapRef.current = null;
      clusterRef.current = null;
      assetLayerRef.current = null;
    };
  }, []);

  // Candidate pins
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (clusterRef.current) {
      map.removeLayer(clusterRef.current);
      clusterRef.current = null;
    }

    const mappable = candidates.filter(hasCoords);
    if (mappable.length === 0) return;

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 42,
      disableClusteringAtZoom: 18,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: clusterIcon
    });

    mappable.forEach(candidate => {
      const isSelected = selected?.id === candidate.id;
      const marker = L.marker(
        [Number(candidate.latitude), Number(candidate.longitude)],
        { icon: pinIcon(isSelected), zIndexOffset: isSelected ? 1000 : 0 }
      );
      marker.bindTooltip(candidate.name || 'Imported location', {
        direction: 'top',
        offset: [0, -30]
      });
      marker.on('click', () => onSelectRef.current(candidate));
      cluster.addLayer(marker);
    });

    cluster.addTo(map);
    clusterRef.current = cluster;

    if (!selected) {
      try {
        map.fitBounds(cluster.getBounds().pad(0.15), { maxZoom: 17 });
      } catch (_) {}
    }
  }, [candidates, selected]);

  // Fly to the selected candidate
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selected || !hasCoords(selected)) return;
    map.flyTo([Number(selected.latitude), Number(selected.longitude)], 19, {
      duration: 0.8
    });
  }, [selected]);

  // Existing site assets as colored dots
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (assetLayerRef.current) {
      map.removeLayer(assetLayerRef.current);
      assetLayerRef.current = null;
    }
    if (!selected) return;

    const layer = L.layerGroup();
    ASSET_TYPES.forEach(type => {
      (assets[type.key] || []).forEach((asset, index) => {
        const coords = assetCoords(asset);
        if (!coords) return;
        L.circleMarker(coords, {
          radius: 7,
          color: '#fff',
          weight: 2,
          fillColor: type.color,
          fillOpacity: 0.95
        })
          .bindTooltip(`${type.label}: ${assetLabel(asset, type.value, index)}`, {
            direction: 'top',
            offset: [0, -8]
          })
          .addTo(layer);
      });
    });
    layer.addTo(map);
    assetLayerRef.current = layer;
  }, [assets, selected]);

  return <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />;
};

const overlayCardStyle = {
  background: 'rgba(255, 255, 255, 0.96)',
  backdropFilter: 'blur(6px)',
  borderRadius: 10,
  boxShadow: '0 6px 24px rgba(15, 23, 42, 0.28)',
  border: '1px solid rgba(15, 23, 42, 0.08)'
};

const ImportedLocationReviewDialog = ({ open, onClose, onCountChange, url }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [assets, setAssets] = useState(emptyAssets);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetType, setAssetType] = useState('controller');
  const [assetId, setAssetId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const fetchCandidates = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${url}/api/irrigation/location-review-candidates`,
        { params: { status: 'pending', limit: 1000 } }
      );
      const nextCandidates = response?.data?.candidates || [];
      setCandidates(nextCandidates);
      if (onCountChange) {
        onCountChange(response?.data?.total ?? nextCandidates.length);
      }
    } catch (requestError) {
      console.error('Error fetching imported irrigation locations', requestError);
      setError(
        requestError?.response?.data?.message ||
          'Unable to load imported irrigation locations.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCandidates();
    } else {
      setSelectedCandidate(null);
      setAssets(emptyAssets);
      setAssetId('');
      setQuery('');
      setError('');
    }
  }, [open]);

  const filteredCandidates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return candidates;
    return candidates.filter(candidate =>
      [
        candidate.name,
        candidate.address,
        siteLabel(candidate),
        siteAddress(candidate)
      ]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(normalizedQuery))
    );
  }, [candidates, query]);

  const groupedCandidates = useMemo(() => {
    const groups = new Map();
    filteredCandidates.forEach(candidate => {
      const label = siteLabel(candidate);
      if (!groups.has(label)) {
        groups.set(label, { label, address: siteAddress(candidate), items: [] });
      }
      groups.get(label).items.push(candidate);
    });
    return Array.from(groups.values());
  }, [filteredCandidates]);

  const selectedTypeConfig = ASSET_TYPES.find(item => item.value === assetType);
  const assetOptions = assets[selectedTypeConfig?.key] || [];
  const plottedAssetTypes = selectedCandidate
    ? ASSET_TYPES.filter(type => (assets[type.key] || []).some(assetCoords))
    : [];

  const handleSelectCandidate = async candidate => {
    setSelectedCandidate(candidate);
    setAssets(emptyAssets);
    setAssetId('');
    setError('');
    if (!candidate?.siteId) return;

    setAssetsLoading(true);
    try {
      const response = await axios.get(
        `${url}/api/irrigation/site/${candidate.siteId}`
      );
      setAssets({ ...emptyAssets, ...(response?.data?.assets || {}) });
    } catch (requestError) {
      console.error('Error fetching jobsite irrigation assets', requestError);
      setError(
        requestError?.response?.data?.message ||
          'Unable to load this jobsite’s irrigation assets.'
      );
    } finally {
      setAssetsLoading(false);
    }
  };

  const removeResolvedCandidate = candidateId => {
    setCandidates(current =>
      current.filter(candidate => String(candidate.id) !== String(candidateId))
    );
    setSelectedCandidate(null);
    setAssets(emptyAssets);
    setAssetId('');
    if (onCountChange) {
      onCountChange(current => Math.max(0, Number(current || 0) - 1));
    }
  };

  const resolveCandidate = async action => {
    if (!selectedCandidate?.id) return;
    setBusy(true);
    setError('');
    try {
      const payload = { action };
      if (action === 'link') {
        payload.assetType = assetType;
        payload.assetId = assetId;
      } else if (action === 'create') {
        payload.assetType = assetType;
      }

      await axios.patch(
        `${url}/api/irrigation/location-review-candidates/${selectedCandidate.id}`,
        payload
      );
      removeResolvedCandidate(selectedCandidate.id);
    } catch (requestError) {
      console.error('Error resolving imported irrigation location', requestError);
      setError(
        requestError?.response?.data?.message ||
          'Unable to update this imported location.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    if (
      window.confirm(
        'Dismiss this imported location? It will be removed from the review queue without changing any assets.'
      )
    ) {
      resolveCandidate('dismiss');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ style: { height: '88vh', overflow: 'hidden' } }}
    >
      <Box
        px={2.5}
        py={1.5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ borderBottom: '1px solid rgba(15, 23, 42, 0.1)' }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <RoomIcon style={{ color: ACCENT, fontSize: 28 }} />
          <Box>
            <Typography variant="h6" style={{ lineHeight: 1.2 }}>
              Imported Irrigation Locations
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Review Google Maps imports before adding them to the irrigation asset record.
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchCandidates} disabled={loading || busy} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose} disabled={busy} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box display="flex" style={{ flex: 1, minHeight: 0 }}>
        {/* Left rail: search + grouped queue */}
        <Box
          style={{
            width: 350,
            minWidth: 300,
            borderRight: '1px solid rgba(15, 23, 42, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            background: '#FAFAF8'
          }}
        >
          <Box px={2} pt={2} pb={1.5}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search location or jobsite"
              value={query}
              onChange={event => setQuery(event.target.value)}
              InputProps={{
                style: { background: '#fff' },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <Box mt={1} display="flex" style={{ gap: 8 }}>
              <Chip
                size="small"
                label={`${filteredCandidates.length} pending`}
                style={{ background: ACCENT, color: '#fff', fontWeight: 600 }}
              />
              {groupedCandidates.length > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${groupedCandidates.length} jobsite${
                    groupedCandidates.length === 1 ? '' : 's'
                  }`}
                />
              )}
            </Box>
          </Box>
          <Box style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={5}>
                <CircularProgress style={{ color: ACCENT }} />
              </Box>
            ) : filteredCandidates.length === 0 ? (
              <Box p={4} textAlign="center">
                <Typography color="textSecondary">
                  {query ? 'No matching locations.' : 'No locations need review.'}
                </Typography>
              </Box>
            ) : (
              groupedCandidates.map(group => (
                <React.Fragment key={group.label}>
                  <ListSubheader
                    component="div"
                    style={{
                      background: '#F1EFE9',
                      borderTop: '1px solid rgba(15, 23, 42, 0.06)',
                      borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                      lineHeight: 1.3,
                      padding: '8px 16px'
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{
                        fontWeight: 700,
                        color: '#44403C',
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        display: 'block'
                      }}
                    >
                      {group.label}
                      <span style={{ color: '#A8A29E', marginLeft: 6 }}>
                        {group.items.length}
                      </span>
                    </Typography>
                    {group.address && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block', fontWeight: 400 }}
                      >
                        {group.address}
                      </Typography>
                    )}
                  </ListSubheader>
                  {group.items.map(candidate => {
                    const isSelected = selectedCandidate?.id === candidate.id;
                    return (
                      <Box
                        key={candidate.id}
                        onClick={() => handleSelectCandidate(candidate)}
                        px={2}
                        py={1}
                        display="flex"
                        alignItems="center"
                        style={{
                          cursor: 'pointer',
                          gap: 10,
                          background: isSelected ? 'rgba(234, 88, 12, 0.10)' : '#fff',
                          borderLeft: isSelected
                            ? `3px solid ${ACCENT}`
                            : '3px solid transparent',
                          borderBottom: '1px solid rgba(15, 23, 42, 0.05)'
                        }}
                      >
                        <RoomIcon
                          fontSize="small"
                          style={{ color: isSelected ? ACCENT : '#D6D3D1' }}
                        />
                        <Box style={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            style={{ fontWeight: isSelected ? 600 : 500 }}
                          >
                            {candidate.name || 'Imported location'}
                          </Typography>
                          {hasCoords(candidate) && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              style={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {Number(candidate.latitude).toFixed(5)},{' '}
                              {Number(candidate.longitude).toFixed(5)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </Box>
        </Box>

        {/* Right: full-bleed map with floating panels */}
        <Box style={{ flex: 1, position: 'relative', minWidth: 0 }}>
          <ReviewMap
            candidates={filteredCandidates}
            selected={selectedCandidate}
            assets={assets}
            onSelect={handleSelectCandidate}
          />

          {!selectedCandidate && !loading && (
            <Box
              style={{
                ...overlayCardStyle,
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '8px 18px',
                zIndex: 1000,
                pointerEvents: 'none'
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Click a pin or pick from the list to review a location.
              </Typography>
            </Box>
          )}

          {selectedCandidate && (
            <>
              <Box
                style={{
                  ...overlayCardStyle,
                  position: 'absolute',
                  top: 14,
                  left: 14,
                  maxWidth: 330,
                  padding: '10px 16px',
                  zIndex: 1000
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: ACCENT,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8
                  }}
                >
                  Suggested jobsite
                </Typography>
                <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {siteLabel(selectedCandidate)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {siteAddress(selectedCandidate) || 'No jobsite address'}
                </Typography>
                {plottedAssetTypes.length > 0 && (
                  <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                    {plottedAssetTypes.map(type => (
                      <Box
                        key={type.value}
                        display="flex"
                        alignItems="center"
                        style={{ gap: 4 }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: type.color,
                            border: '1.5px solid #fff',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.15)'
                          }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {type.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box
                style={{
                  ...overlayCardStyle,
                  position: 'absolute',
                  left: 14,
                  right: 14,
                  bottom: 14,
                  padding: '14px 18px',
                  zIndex: 1000,
                  maxHeight: '55%',
                  overflowY: 'auto'
                }}
              >
                <Box
                  display="flex"
                  alignItems="baseline"
                  flexWrap="wrap"
                  style={{ gap: 8 }}
                >
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {selectedCandidate.name || 'Imported location'}
                  </Typography>
                  {hasCoords(selectedCandidate) && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {Number(selectedCandidate.latitude).toFixed(6)},{' '}
                      {Number(selectedCandidate.longitude).toFixed(6)}
                    </Typography>
                  )}
                </Box>
                {selectedCandidate.address && (
                  <Typography variant="caption" color="textSecondary">
                    {selectedCandidate.address}
                  </Typography>
                )}

                <Box display="flex" style={{ gap: 12, marginTop: 14 }}>
                  <TextField
                    select
                    label="Asset type"
                    variant="outlined"
                    size="small"
                    value={assetType}
                    onChange={event => {
                      setAssetType(event.target.value);
                      setAssetId('');
                    }}
                    style={{ minWidth: 150 }}
                  >
                    {ASSET_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    label={assetsLoading ? 'Loading assets…' : 'Existing asset'}
                    variant="outlined"
                    size="small"
                    value={assetId}
                    disabled={assetsLoading || assetOptions.length === 0}
                    onChange={event => setAssetId(event.target.value)}
                    helperText={
                      !assetsLoading && assetOptions.length === 0
                        ? `No existing ${selectedTypeConfig?.label.toLowerCase()} assets at this jobsite.`
                        : undefined
                    }
                  >
                    {assetOptions.map((asset, index) => {
                      const value = asset._id || asset.serverId || asset.id;
                      return (
                        <MenuItem key={value} value={value}>
                          {assetLabel(asset, assetType, index)}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Box>

                {error && (
                  <Typography color="error" variant="body2" style={{ marginTop: 10 }}>
                    {error}
                  </Typography>
                )}

                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  style={{ gap: 10, marginTop: 14 }}
                >
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<LinkIcon />}
                    disabled={busy || !assetId}
                    onClick={() => resolveCandidate('link')}
                    style={
                      busy || !assetId
                        ? undefined
                        : { background: ACCENT, color: '#fff' }
                    }
                  >
                    Link existing
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddLocationIcon />}
                    disabled={busy}
                    onClick={() => resolveCandidate('create')}
                    style={busy ? undefined : { borderColor: ACCENT, color: ACCENT }}
                  >
                    Create {selectedTypeConfig?.label}
                  </Button>
                  <Button
                    variant="text"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={busy}
                    onClick={handleDismiss}
                    style={{ color: '#78716C' }}
                  >
                    Dismiss
                  </Button>
                  {busy && <CircularProgress size={22} style={{ color: ACCENT }} />}
                </Box>
              </Box>
            </>
          )}

          {error && !selectedCandidate && (
            <Box
              style={{
                ...overlayCardStyle,
                position: 'absolute',
                left: 14,
                right: 14,
                bottom: 14,
                padding: '10px 16px',
                zIndex: 1000
              }}
            >
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default ImportedLocationReviewDialog;
